import type { App, DirectiveBinding, VNode } from "vue";
import type { FeatureFlagService } from "../services/FeatureFlagService";

/**
 * Vue directive for conditionally rendering elements based on feature flags.
 *
 * @example
 * <!-- Basic usage -->
 * <div v-feature-flag="'new-feature'">
 *   New feature content
 * </div>
 *
 * @example
 * <!-- With modifier for inverted logic -->
 * <div v-feature-flag:not="'maintenance-mode'">
 *   Content shown when maintenance mode is OFF
 * </div>
 */

interface FeatureFlagElement extends HTMLElement {
  _flagpole?: {
    flagName: string;
    originalDisplay: string;
    isInverted: boolean;
  };
}

function updateElementVisibility(
  el: FeatureFlagElement,
  isEnabled: boolean,
  isInverted: boolean
): void {
  const shouldShow = isInverted ? !isEnabled : isEnabled;

  if (shouldShow) {
    el.style.display = el._flagpole?.originalDisplay || "";
  } else {
    if (!el._flagpole?.originalDisplay) {
      el._flagpole!.originalDisplay = el.style.display || "";
    }
    el.style.display = "none";
  }
}

export const featureFlagDirective = {
  mounted(el: FeatureFlagElement, binding: DirectiveBinding, vnode: VNode) {
    const app = vnode.appContext?.app;
    if (!app) return;

    const service = app.config.globalProperties
      .$flagpoleService as FeatureFlagService;
    if (!service) {
      console.warn(
        "[FlagPole SDK] FeatureFlagService not found. Make sure to install the plugin."
      );
      return;
    }

    const flagName = binding.value as string;
    const isInverted = binding.arg === "not";

    if (!flagName || typeof flagName !== "string") {
      console.warn(
        "[FlagPole SDK] v-feature-flag directive requires a string flag name"
      );
      return;
    }

    // Store directive metadata
    el._flagpole = {
      flagName,
      originalDisplay: el.style.display || "",
      isInverted,
    };

    // Initial update
    const isEnabled = service.isFeatureEnabled(flagName);
    updateElementVisibility(el, isEnabled, isInverted);

    // Watch for changes
    const unwatchFlags = service.flags.value;
    // Note: In a real implementation, you'd want to watch the reactive flags
    // This is a simplified version
  },

  updated(el: FeatureFlagElement, binding: DirectiveBinding, vnode: VNode) {
    const app = vnode.appContext?.app;
    if (!app || !el._flagpole) return;

    const service = app.config.globalProperties
      .$flagpoleService as FeatureFlagService;
    if (!service) return;

    const flagName = binding.value as string;
    const isInverted = binding.arg === "not";

    // Update flag name if changed
    if (flagName !== el._flagpole.flagName) {
      el._flagpole.flagName = flagName;
    }

    if (isInverted !== el._flagpole.isInverted) {
      el._flagpole.isInverted = isInverted;
    }

    // Update visibility
    const isEnabled = service.isFeatureEnabled(flagName);
    updateElementVisibility(el, isEnabled, isInverted);
  },

  unmounted(el: FeatureFlagElement) {
    // Cleanup
    delete el._flagpole;
  },
};

export function createFeatureFlagDirective() {
  return {
    install(app: App) {
      app.directive("feature-flag", featureFlagDirective);
    },
  };
}
